import { darkModeColors } from '../../util';
import { ChartGradient } from '../types';
import type { Color } from 'echarts/core';

const elementsGradientBuilder = (colorAt0: string, colorAt100: string): Color => ({
  type: 'linear',
  x: 0,
  y: 0,
  x2: 1,
  y2: 1,
  colorStops: [
    {
      offset: 0,
      color: colorAt0, // color at 0%
    },
    {
      offset: 1,
      color: colorAt100, // color at 0%
    },
  ],
});

export interface ElementGradientMapArgs {
  accountLighter?: string;
  accountDarker?: string;
}

export const elementGradientMap = ({ accountDarker, accountLighter }: ElementGradientMapArgs) =>
  new Map<ChartGradient, Color>([
    ['Ir', elementsGradientBuilder('#169FFF', '#0057A8')],
    ['Tr', elementsGradientBuilder('#FFBE00', '#FF8C0E')],
    ['Lt', elementsGradientBuilder('#FF8C0E', '#FF4F03')],
    ['Bt', elementsGradientBuilder('#FF144F', '#A8005B')],
    ['Qt', elementsGradientBuilder('#8B31F1', '#5B00C1')],
    ['Rt', elementsGradientBuilder('#284ECD', '#0029B3')],
    ['Sr', elementsGradientBuilder('#31948F', '#005662')],
    ['Dr', elementsGradientBuilder('#016E42', '#004607')],
    ['Er', elementsGradientBuilder('#16A104', '#005F3D')],
    ['Tt', elementsGradientBuilder('#93CE00', '#4F8D00')],
    ['netWorth', elementsGradientBuilder('#00F58D', '#8B49EC')],
    ['netWorth-negative', elementsGradientBuilder('#8B49EC', '#C036DD')],
    [
      'account',
      elementsGradientBuilder(
        accountDarker ?? darkModeColors.almostBlack,
        accountLighter ?? darkModeColors.almostBlack
      ),
    ],
    [
      'account-negative',
      elementsGradientBuilder(
        accountLighter ?? darkModeColors.almostBlack,
        accountDarker ?? darkModeColors.almostBlack
      ),
    ],
  ]);

const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
  let parsedHex = hex.replace('#', '');
  if (parsedHex.length === 3) {
    parsedHex = parsedHex
      .split('')
      .map((char) => char + char)
      .join('');
  }

  const r = parseInt(parsedHex.substring(0, 2), 16);
  const g = parseInt(parsedHex.substring(2, 4), 16);
  const b = parseInt(parsedHex.substring(4, 6), 16);

  return { r, g, b };
};

const rgbToHex = ({ r, g, b }: { r: number; g: number; b: number }): string => {
  const toHex = (value: number): string => {
    const hex = value.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

export const adjustColor = (hex: string, percentage: number): string => {
  const { r, g, b } = hexToRgb(hex);

  const adjustValue = (value: number): number => {
    const newValue = value + value * percentage;
    return Math.min(255, Math.max(0, Math.round(newValue)));
  };

  const adjustedR = adjustValue(r);
  const adjustedG = adjustValue(g);
  const adjustedB = adjustValue(b);

  return rgbToHex({ r: adjustedR, g: adjustedG, b: adjustedB });
};

export const getElementGradientMap = (p: { lightColor: string; baseColor: string }) => {
  return new Map<string, Color>([['firm-color', elementsGradientBuilder(p.baseColor, p.lightColor)]]);
};
